import { PartialId, Word } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as dms from '../dms'

/** Maps fields from one API response object to one DMS record. */
export interface DmsMapping {
  /**
   * DMS entity or snapshot that receives the imported records.
   *
   * @example 'ent:!jira:!issue'
   */
  modelId: dms.models.ForRecord.Id
  /** @see {@link DmsMapping.Field} */
  fields: readonly DmsMapping.Field[]
}
export namespace DmsMapping {
  /** Result of mapping one third-party API object into DMS. */
  export interface Result {
    /** DMS entity or snapshot that receives the record. */
    modelId: dms.models.ForRecord.Id
    /** DMS record produced from the configured field mappings. */
    record: dms.data.Record.Input
  }

  /** Maps one third-party API object into a DMS record. */
  export function map(
    mapping: Readonly<DmsMapping>,
    source: Readonly<Record<string, unknown>>,
  ): Result {
    return {
      modelId: mapping.modelId,
      record: {
        values: Object.fromEntries(
          mapping.fields.map(field => [field.target, Field.map(field, source)]),
        ),
      },
    }
  }

  export const isDmsMapping: TypeChecker<DmsMapping> = TypeChecker.Factory.lazy(
    () =>
      TypeChecker.Factory.object<DmsMapping>({
        fields: TypeChecker.Factory.list(Field.isField),
        modelId: dms.models.ForRecord.Id.isId,
      }),
  ).usingAlias('integrations.DmsMapping')

  export const assertIsDmsMapping: TypeAsserter<DmsMapping> =
    TypeAsserter.Factory.from(isDmsMapping)

  /**
   * Copies one source property into one DMS attribute, verbatim unless
   * {@link Field.reference} asks for it to be shaped into a reference.
   */
  export interface Field {
    /**
     * Dot-separated property path in the API response object.
     *
     * @example 'key' for `{ key: 'ATTAIN-123' }`
     * @example 'fields.summary' for `{ fields: { summary: 'Import Jira issues' } }`
     * @example 'fields.status.name' for `{ fields: { status: { name: 'In Progress' } } }`
     * @example 'fields.assignee.accountId' for `{ fields: { assignee: { accountId: 'abc123' } } }`
     * @example 'fields.project.key' for `{ fields: { project: { key: 'ATTAIN' } } }`
     */
    source: string
    /**
     * DMS attribute ID that receives the value. For Jira issues, the issue
     * `key` can be mapped to `!id` when it should identify the DMS record.
     *
     * @example '!name'
     * @example '!id'
     */
    target: dms.data.Record.Values.Key
    /**
     * Set when {@link Field.target} is a Reference attribute. The source value
     * is then read as the ID of a record in {@link Field.Reference.entityId}
     * rather than as the attribute's value.
     *
     * @see {@link Field.Reference}
     */
    reference?: Field.Reference
  }
  export namespace Field {
    /**
     * Builds a {@link dms.data.Value.Reference.Default} from the source value,
     * which a plain copy cannot do because a Reference attribute stores a
     * structured `{ entityId, recordId }` object rather than a bare value.
     */
    export interface Reference {
      /**
       * Entity holding the referenced record.
       *
       * @example 'ent:!epics:!epic'
       */
      entityId: dms.models.Entity.Id
    }
    export namespace Reference {
      /**
       * Shapes a raw source value into the object a Reference attribute stores.
       *
       * The record ID goes through the same {@link PartialId.from} that
       * {@link dms.data.Record.Values.Input.WithValidIds.from} applies to
       * `!id`, so a reference and the record it points at always agree on the
       * ID. That call is also idempotent, leaving a source value that is
       * already a {@link PartialId} untouched.
       */
      export function toValue(
        reference: Readonly<Reference>,
        value: unknown,
      ): dms.data.Value.Reference.Default | undefined {
        // Anything that is not an ID-like scalar cannot name a record.
        if (!TypeChecker.isString(value) && !TypeChecker.isNumber(value)) {
          return undefined
        }
        const recordId = String(value)
        // `PartialId.Custom.create` would build an unusable ID from a suffix
        // with no word characters, so reject those rather than store one.
        if (!Word.isWord(recordId)) {
          return undefined
        }
        return {
          entityId: reference.entityId,
          recordId: PartialId.from(recordId),
        }
      }

      export const isReference: TypeChecker<Reference> =
        TypeChecker.Factory.object<Reference>({
          entityId: dms.models.Entity.Id.isId,
        }).usingAlias('integrations.DmsMapping.Field.Reference')

      export const assertIsReference: TypeAsserter<Reference> =
        TypeAsserter.Factory.from(isReference)
    }

    /**
     * Reads the configured source property from a third-party API object, and
     * shapes it into a reference when {@link Field.reference} says to.
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function map(
      field: Readonly<Field>,
      source: Readonly<Record<string, unknown>>,
    ): dms.data.Value | undefined {
      let value: unknown = source
      for (const property of field.source.split('.')) {
        if (!TypeChecker.isObject(value)) {
          return undefined
        }
        value = (value as Readonly<Record<string, unknown>>)[property]
      }
      if (!field.reference) {
        return value
      }
      return Reference.toValue(field.reference, value)
    }

    export const isField: TypeChecker<Field> =
      TypeChecker.Factory.object<Field>({
        reference: TypeChecker.Factory.optional(Reference.isReference),
        source: TypeChecker.isString,
        target: PartialId.isPartialId,
      }).usingAlias('integrations.DmsMapping.Field')

    export const assertIsField: TypeAsserter<Field> =
      TypeAsserter.Factory.from(isField)
  }
}
